import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { teamsAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/TeamsPage.module.css';

const TeamsPage = () => {
  const { can, refreshPermissions } = useAuth();
  const { t } = useLocalization();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [editTeam, setEditTeam] = useState({ id: '', name: '', description: '', status: 'active' });
  const [updatingMember, setUpdatingMember] = useState(false);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setTeams([]);
        setErrorMessage(t('teams_permission_view_denied', 'You no longer have permission to view teams.'));
        refreshPermissions();
        return;
      }
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshPermissions, t]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        refreshPermissions();
        return;
      }
      console.error('Failed to fetch users:', error);
    }
  }, [refreshPermissions]);

  useEffect(() => {
    if (!can('teams', 'view')) {
      setLoading(false);
      setErrorMessage(t('teams_permission_view_denied', 'You no longer have permission to view teams.'));
      return;
    }

    fetchTeams();
    if (can('users', 'view')) {
      fetchUsers();
    }
  }, [can, t, fetchTeams, fetchUsers]);

  // Auto-select first team when teams load
  useEffect(() => {
    if (teams.length > 0 && !selectedTeam) {
      handleTeamClick(teams[0]);
    }
  }, [teams, selectedTeam]);

  const handleTeamClick = async (team) => {
    setSelectedTeam(team);
    try {
      const [membersResponse] = await Promise.all([
        teamsAPI.getMembers(team.id),
        can('users', 'view') ? fetchUsers() : Promise.resolve(),
      ]);
      setMembers(membersResponse.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await teamsAPI.create(newTeam);
      setNewTeam({ name: '', description: '' });
      setShowCreateModal(false);
      fetchTeams();
    } catch (error) {
      console.error('Failed to create team:', error);
      alert(`${t('teams_create_failed_prefix', 'Error creating team')}: ${error.response?.data?.message || error.message}`);
    }
  };

  const openEditModal = () => {
    if (!selectedTeam) return;
    setEditTeam({
      id: selectedTeam.id,
      name: selectedTeam.name,
      description: selectedTeam.description || '',
      status: selectedTeam.status || 'active',
    });
    setShowEditModal(true);
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await teamsAPI.update(editTeam.id, {
        name: editTeam.name,
        description: editTeam.description,
        status: editTeam.status,
      });
      setShowEditModal(false);
      await fetchTeams();
      const updated = teams.find((t) => t.id === editTeam.id) || selectedTeam;
      await handleTeamClick({ ...updated, ...editTeam });
    } catch (error) {
      alert(error.response?.data?.message || t('teams_update_failed', 'Failed to update team'));
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    if (!window.confirm(`${t('teams_delete_confirm_prefix', 'Delete team')} ${selectedTeam.name}?`)) return;

    try {
      await teamsAPI.delete(selectedTeam.id);
      setSelectedTeam(null);
      setMembers([]);
      await fetchTeams();
    } catch (error) {
      alert(error.response?.data?.message || t('teams_delete_failed', 'Failed to delete team'));
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedTeam) return;
    try {
      await teamsAPI.removeMember(selectedTeam.id, userId);
      await Promise.all([
        handleTeamClick(selectedTeam),
        fetchTeams(),
        can('users', 'view') ? fetchUsers() : Promise.resolve(),
      ]);
    } catch (error) {
      alert(error.response?.data?.message || t('teams_remove_member_failed', 'Failed to remove member'));
    }
  };

  const memberUserIds = new Set(members.map((m) => m.user_id));
  const availableUsers = users
    .filter((user) => {
      return user.status === 'active'
        && user.role !== 'admin'
        && !memberUserIds.has(user.id)
        && !user.active_team_id;
    });

  const getUserUnavailableReason = (user) => {
    if (user.active_team_id && Number(user.active_team_id) !== Number(selectedTeam?.id)) {
      return `${t('teams_assigned_to', 'Assigned to')} ${user.active_team_name || t('teams_another_team', 'another team')}`;
    }

    return '';
  };

  const canAddUserToSelectedTeam = (user) => !getUserUnavailableReason(user);
  const teamMembers = members.map((member) => ({
    id: member.user_id,
    username: member.username,
    email: member.email,
    firstName: member.first_name,
    lastName: member.last_name,
  }));

  const handleMemberDragEnd = async (result) => {
    if (!can('teams', 'edit') || updatingMember) return;

    const { source, destination } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    if (!selectedTeam) return;

    try {
      setUpdatingMember(true);

      if (source.droppableId === 'availableUsers' && destination.droppableId === 'teamMembers') {
        const user = availableUsers[source.index];
        if (!user) return;
        if (!canAddUserToSelectedTeam(user)) {
          alert(`${t('teams_cannot_add', 'Cannot add')} ${user.username}: ${getUserUnavailableReason(user)}`);
          return;
        }
        await teamsAPI.addMember(selectedTeam.id, user.id);
      }

      if (source.droppableId === 'teamMembers' && destination.droppableId === 'availableUsers') {
        const user = teamMembers[source.index];
        if (!user) return;
        await teamsAPI.removeMember(selectedTeam.id, user.id);
      }

      await Promise.all([
        handleTeamClick(selectedTeam),
        fetchTeams(),
        can('users', 'view') ? fetchUsers() : Promise.resolve(),
      ]);
    } catch (error) {
      alert(error.response?.data?.message || t('teams_move_member_failed', 'Failed to move team member'));
    } finally {
      setUpdatingMember(false);
    }
  };

  const handleAddMember = async (user) => {
    if (!selectedTeam) return;
    if (!canAddUserToSelectedTeam(user)) {
      alert(`${t('teams_cannot_add', 'Cannot add')} ${user.username}: ${getUserUnavailableReason(user)}`);
      return;
    }

    try {
      await teamsAPI.addMember(selectedTeam.id, user.id);
      await Promise.all([
        handleTeamClick(selectedTeam),
        fetchTeams(),
        can('users', 'view') ? fetchUsers() : Promise.resolve(),
      ]);
    } catch (error) {
      alert(error.response?.data?.message || t('teams_add_member_failed', 'Failed to add member'));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('teams_page_title', 'Teams Management')}</h1>
        {can('teams', 'create') && (
          <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>+ {t('teams_create_team', 'Create Team')}</button>
        )}
      </div>

      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

      <div className={styles.content}>
        <div className={styles.teamsList}>
          {loading ? (
            <p>{t('teams_loading', 'Loading teams...')}</p>
          ) : (
            teams.map((team) => (
              <div
                key={team.id}
                className={`${styles.teamCard} ${selectedTeam?.id === team.id ? styles.selected : ''}`}
                onClick={() => handleTeamClick(team)}
              >
                <h3>{team.name}</h3>
                <p className={styles.memberCount}>{team.member_count} {t('teams_members', 'members')}</p>
                <p className={styles.supervisor}>{team.supervisor_name}</p>
              </div>
            ))
          )}
        </div>

        {selectedTeam && (
          <div className={styles.teamDetails}>
            <div className={styles.detailHeader}>
              <h2>{selectedTeam.name}</h2>
              {(can('teams', 'edit') || can('teams', 'delete')) && (
                <div className={styles.actionButtons}>
                  {can('teams', 'edit') && (
                    <button className={styles.smallBtn} onClick={openEditModal}>{t('teams_edit', 'Edit')}</button>
                  )}
                  {can('teams', 'delete') && (
                    <button className={styles.smallDangerBtn} onClick={handleDeleteTeam}>{t('teams_delete', 'Delete')}</button>
                  )}
                </div>
              )}
            </div>
            <p>{selectedTeam.description}</p>

            <div className={styles.membersSection}>
              <h3>{t('teams_members_title', 'Team Members')} ({members.length})</h3>

              {can('teams', 'edit') && can('users', 'view') ? (
                <>
                  <p className={styles.kanbanHint}>{t('teams_kanban_hint', 'Drag users between columns or use the Add / Remove buttons to manage team members.')}</p>
                  <DragDropContext onDragEnd={handleMemberDragEnd}>
                    <div className={styles.memberKanban}>
                      <div className={styles.memberColumn}>
                        <div className={styles.memberColumnHeader}>{t('teams_available_users', 'Available Users')} ({availableUsers.length})</div>
                        <Droppable droppableId="availableUsers">
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`${styles.memberColumnBody} ${snapshot.isDraggingOver ? styles.dragOver : ''}`}
                            >
                              {availableUsers.map((user, index) => (
                                <Draggable key={`available-${user.id}`} draggableId={`available-${user.id}`} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      className={`${styles.memberCard} ${dragSnapshot.isDragging ? styles.memberDragging : ''}`}
                                    >
                                      <p className={styles.memberName}>{user.first_name} {user.last_name}</p>
                                      <p className={styles.memberEmail}>{user.email}</p>
                                      {getUserUnavailableReason(user) ? (
                                        <p className={styles.memberWarning}>{getUserUnavailableReason(user)}</p>
                                      ) : null}
                                      <button
                                        className={styles.memberActionBtn}
                                        onClick={() => handleAddMember(user)}
                                        disabled={!canAddUserToSelectedTeam(user)}
                                      >
                                        {t('common_add', 'Add')}
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>

                      <div className={styles.memberColumn}>
                        <div className={styles.memberColumnHeader}>{t('teams_members_title', 'Team Members')} ({teamMembers.length})</div>
                        <Droppable droppableId="teamMembers">
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`${styles.memberColumnBody} ${snapshot.isDraggingOver ? styles.dragOver : ''}`}
                            >
                              {teamMembers.map((member, index) => (
                                <Draggable key={`member-${member.id}`} draggableId={`member-${member.id}`} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      className={`${styles.memberCard} ${dragSnapshot.isDragging ? styles.memberDragging : ''}`}
                                    >
                                      <p className={styles.memberName}>{member.firstName} {member.lastName}</p>
                                      <p className={styles.memberEmail}>{member.email}</p>
                                      <button
                                        className={styles.memberActionBtn}
                                        onClick={() => handleRemoveMember(member.id)}
                                      >
                                        {t('teams_remove', 'Remove')}
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>
                  </DragDropContext>
                </>
              ) : (
                <div className={styles.membersList}>
                  {members.map((member) => (
                    <div key={member.id} className={styles.memberCard}>
                      <p className={styles.memberName}>{member.first_name} {member.last_name}</p>
                      <p className={styles.memberEmail}>{member.email}</p>
                      {can('teams', 'edit') && (
                        <button
                          className={styles.removeMemberBtn}
                          onClick={() => handleRemoveMember(member.user_id)}
                        >
                          {t('teams_remove', 'Remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('teams_modal_create_title', 'Create New Team')}</h2>
            <form onSubmit={handleCreateTeam}>
              <div className={styles.formGroup}>
                <label>{t('teams_label_name', 'Team Name')} *</label>
                <input
                  type="text"
                  required
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder={t('teams_placeholder_name', 'Enter team name')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('teams_label_description', 'Description')}</label>
                <textarea
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  placeholder={t('teams_placeholder_description', 'Enter team description')}
                />
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>{t('teams_create_team', 'Create Team')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowCreateModal(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('teams_modal_edit_title', 'Edit Team')}</h2>
            <form onSubmit={handleUpdateTeam}>
              <div className={styles.formGroup}>
                <label>{t('teams_label_name', 'Team Name')} *</label>
                <input
                  type="text"
                  required
                  value={editTeam.name}
                  onChange={(e) => setEditTeam({ ...editTeam, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('teams_label_description', 'Description')}</label>
                <textarea
                  value={editTeam.description}
                  onChange={(e) => setEditTeam({ ...editTeam, description: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('teams_label_status', 'Status')}</label>
                <select
                  value={editTeam.status}
                  onChange={(e) => setEditTeam({ ...editTeam, status: e.target.value })}
                >
                  <option value="active">{t('common_status_active', 'Active')}</option>
                  <option value="inactive">{t('common_status_inactive', 'Inactive')}</option>
                </select>
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>{t('common_save', 'Save')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowEditModal(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsPage;
